// Single source of truth for per-listing SEO (title, meta description, canonical,
// H1, JSON-LD). Pure functions, NO imports: this file is used BOTH by the React
// app (CarDetail -> <Seo/>) and by the build-time prerender script
// (scripts/prerender-seo.mjs), so the HTML served by the static host and the
// HTML after hydration carry exactly the same tags.

export const SEO_SITE = "https://pactlycar.be";

// Must stay byte-identical to src/lib/slug.js (slugify / carSlug).
export function seoSlugify(str) {
  return String(str || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 70);
}

export function seoCarSlug(car) {
  if (!car || !car.id) return "";
  const label = [car.make, car.model, car.year].filter(Boolean).join(" ");
  const s = seoSlugify(label);
  return s ? `${s}--${car.id}` : String(car.id);
}

// Same behaviour as cleanTitle() in src/lib/format.js.
function esc(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }
export function seoCleanName(make, model) {
  const mk = String(make || "").trim();
  const md = String(model || "").trim();
  if (!md) return mk;
  if (!mk) return md;
  const mkl = mk.toLowerCase();
  const mdl = md.toLowerCase();
  if (mdl === mkl) return mk;
  if (mdl.startsWith(mkl + " ")) return md;
  const collapsed = md.replace(new RegExp(`^(${esc(mk)})\\s+\\1\\b`, "i"), "$1");
  return `${mk} ${collapsed}`.replace(new RegExp(`^${esc(mk)}\\s+${esc(mk)}\\b`, "i"), mk);
}

// Thousands with a plain space: 54000 -> "54 000"
export function seoNum(n) {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return "";
  return String(v).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

// Year: explicit year, else derived from first_registration ("04/2021", "2021-04", "2021").
export function seoYear(car) {
  const y = Number(car && car.year);
  if (Number.isInteger(y) && y > 1900 && y < 2100) return y;
  const m = String((car && car.first_registration) || "").match(/(19|20)\d{2}/);
  return m ? Number(m[0]) : null;
}

function firstRegIso(car) {
  const s = String((car && car.first_registration) || "");
  let m = s.match(/^(\d{1,2})[/.-]((?:19|20)\d{2})$/);
  if (m) return `${m[2]}-${String(m[1]).padStart(2, "0")}`;
  m = s.match(/^((?:19|20)\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}`;
  return null;
}

// Feeds mix DE/NL/FR/EN fuel labels ("Benzin", "Elektro/Benzine", "Petrol"...).
// Returns { fr: label for title/description, schema: schema.org fuelType }.
export function seoFuel(raw) {
  const f = String(raw || "").toLowerCase();
  if (!f || f.startsWith("-") || f.includes("sonstige")) return null;
  const elec = /elek|elec|hybr/.test(f);
  if (elec && f.includes("diesel")) return { fr: "hybride diesel", schema: "Hybrid" };
  if (elec && /benz|ess|petrol|gasol/.test(f)) return { fr: "hybride essence", schema: "Hybrid" };
  if (f.includes("hybr")) return { fr: "hybride", schema: "Hybrid" };
  if (/elek|elec/.test(f)) return { fr: "électrique", schema: "Electric" };
  if (f.includes("diesel")) return { fr: "diesel", schema: "Diesel" };
  if (/benz|petrol|essence|gasol/.test(f)) return { fr: "essence", schema: "Gasoline" };
  if (/lpg|autogas|gpl/.test(f)) return { fr: "GPL", schema: "LPG" };
  if (/cng|erdgas|gnc/.test(f)) return { fr: "GNC", schema: "CNG" };
  if (f.includes("ethanol")) return { fr: "éthanol", schema: "Ethanol" };
  return null;
}

function transmission(raw) {
  const t = String(raw || "").toLowerCase();
  if (!t) return null;
  if (t.startsWith("auto")) return { fr: "boîte automatique", schema: "Automatic" };
  if (t.startsWith("man")) return { fr: "boîte manuelle", schema: "Manual" };
  return null;
}

const COUNTRY_FR = {
  BE: "Belgique", FR: "France", NL: "Pays-Bas", DE: "Allemagne", LU: "Luxembourg",
  ES: "Espagne", IT: "Italie", PL: "Pologne", RO: "Roumanie", PT: "Portugal",
  AT: "Autriche", CH: "Suisse",
};

// "86929, Penzing" -> "Penzing" ; "Oostende Zandvoorde +Oostende" -> "Oostende Zandvoorde"
export function seoLocation(raw) {
  let s = String(raw || "").split("+")[0];
  s = s.replace(/\b[A-Z]{1,2}-?\d{4,5}\b/g, " ").replace(/\b\d{4,5}\b/g, " ");
  s = s.replace(/[,;|]+/g, " ").replace(/\s+/g, " ").trim();
  if (!s || s.length < 2 || /^\d+$/.test(s)) return null;
  return s.length > 40 ? s.slice(0, 40).replace(/\s+\S*$/, "") : s;
}

function clip(s, max) {
  if (s.length <= max) return s;
  const cut = s.slice(0, max - 1);
  return cut.replace(/[\s,;:.]+\S*$/, "") + "…";
}

// "Neuf" only when the feed says so AND the odometer agrees (some feeds tag
// 97 000 km cars as "Neuf").
function isNew(car) {
  const c = String((car && car.condition) || "").toLowerCase();
  const flagged = c === "new" || c === "neuf" || c === "nieuw" || (car && car.is_new === true);
  const km = Number(car && car.mileage);
  return flagged && (!Number.isFinite(km) || km < 1000);
}

// Main entry. `opts.sold` forces SoldOut availability.
export function buildListingSeo(car, opts = {}) {
  const name = seoCleanName(car.make, car.model) || "Véhicule";
  const year = seoYear(car);
  const km = Number(car.mileage) > 0 ? `${seoNum(car.mileage)} km` : null;
  const fuel = seoFuel(car.fuel);
  const gear = transmission(car.transmission);
  const price = Number(car.price) > 0 ? Number(car.price) : null;
  const priceTxt = price ? `${seoNum(price)} €` : null;
  const city = seoLocation(car.location);
  const country = COUNTRY_FR[String(car.country_code || "").toUpperCase()] || null;
  const condition = isNew(car) ? "neuf" : "occasion";
  const slug = seoCarSlug(car);
  const canonical = `${SEO_SITE}/car/${slug}`;
  const sold = !!opts.sold || String(car.status || "").toLowerCase() === "sold";

  const nameYear = [name, year].filter(Boolean).join(" ");
  // Keep titles readable (~70 chars before the brand): drop fuel, then km, then clip the name.
  const mkTitle = (n, k, f) => [`${n} ${condition}`, k, f, priceTxt].filter(Boolean).join(", ");
  let core = mkTitle(nameYear, km, fuel && fuel.fr);
  if (core.length > 70) core = mkTitle(nameYear, km, null);
  if (core.length > 70) core = mkTitle(nameYear, null, null);
  if (core.length > 70) core = mkTitle(clip(name, 40) + (year ? ` ${year}` : ""), null, null);
  const title = `${core} | Pactly Car`;

  const where = city ? ` à ${city}${country ? ` (${country})` : ""}` : (country ? ` en ${country}` : "");
  const facts = [km, fuel && fuel.fr, gear && gear.fr, Number(car.power) > 0 ? `${Math.round(car.power)} ch` : null].filter(Boolean).join(", ");
  const lead = `${nameYear} ${condition === "neuf" ? "neuf" : "d'occasion"}${where}`;
  const base = `${lead}${facts ? ` : ${facts}` : ""}.${priceTxt ? ` Prix : ${priceTxt}.` : ""}`;
  const ctaLong = " Photos, détails et contact vendeur sur Pactly Car.";
  const ctaShort = " Sur Pactly Car.";
  const description = (base + ctaLong).length <= 160 ? base + ctaLong
    : (base + ctaShort).length <= 160 ? base + ctaShort : clip(base, 160);
  const h1 = nameYear;

  const images = (Array.isArray(car.images) ? car.images : [])
    .map((im) => (typeof im === "string" ? im : im && (im.url || im.src)))
    .map((u) => (typeof u === "string" && u.startsWith("/") && !u.startsWith("//") ? `${SEO_SITE}${u}` : u))
    .filter((u) => typeof u === "string" && /^https?:\/\//.test(u))
    .slice(0, 5);

  const vt = String(car.vehicle_type || "car").toLowerCase();
  const kind = vt === "car" ? "Car" : vt === "moto" ? "Motorcycle" : "Vehicle";
  const condUrl = condition === "neuf" ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition";
  const reg = firstRegIso(car);
  const vehicle = {
    "@context": "https://schema.org",
    "@type": ["Product", kind],
    "@id": `${canonical}#vehicle`,
    name: nameYear,
    description,
    url: canonical,
    ...(images.length ? { image: images } : {}),
    sku: String(car.ref || car.id),
    brand: { "@type": "Brand", name: String(car.make || name) },
    ...(car.model ? { model: String(car.model) } : {}),
    ...(year ? { vehicleModelDate: String(year), productionDate: String(year) } : {}),
    ...(reg ? { dateVehicleFirstRegistered: reg } : {}),
    ...(car.mileage != null && car.mileage !== "" && Number(car.mileage) >= 0 ? { mileageFromOdometer: { "@type": "QuantitativeValue", value: Math.round(Number(car.mileage)), unitCode: "KMT" } } : {}),
    ...(fuel ? { fuelType: fuel.schema } : {}),
    ...(gear ? { vehicleTransmission: gear.schema } : {}),
    ...(car.body_type ? { bodyType: String(car.body_type) } : {}),
    ...(car.color ? { color: String(car.color) } : {}),
    ...(Number(car.doors) > 0 ? { numberOfDoors: Number(car.doors) } : {}),
    ...(Number(car.power) > 0 ? { vehicleEngine: { "@type": "EngineSpecification", enginePower: { "@type": "QuantitativeValue", value: Math.round(Number(car.power)), unitCode: "BHP" } } } : {}),
    itemCondition: condUrl,
    ...(price ? { offers: {
      "@type": "Offer",
      price,
      priceCurrency: "EUR",
      availability: sold ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
      itemCondition: condUrl,
      url: canonical,
      ...(opts.priceValidUntil ? { priceValidUntil: opts.priceValidUntil } : {}),
      ...(car.seller_name ? { seller: { "@type": "Organization", name: String(car.seller_name) } } : {}),
      ...(city || country ? { availableAtOrFrom: { "@type": "Place", address: { "@type": "PostalAddress", ...(city ? { addressLocality: city } : {}), ...(car.country_code ? { addressCountry: String(car.country_code).toUpperCase() } : {}) } } } : {}),
    } } : {}),
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Pactly Car", item: `${SEO_SITE}/` },
      { "@type": "ListItem", position: 2, name: "Véhicules d'occasion", item: `${SEO_SITE}/search` },
      { "@type": "ListItem", position: 3, name: nameYear, item: canonical },
    ],
  };
  return { title, description, h1, canonical, slug, image: images[0] || null, jsonLd: [vehicle, breadcrumb], facts, where: where.trim(), priceTxt };
}
